import { PureComponent, ReactNode } from 'react';
import { Redirect, Route, Switch } from 'react-router-dom';
import {
    GenieKeys,
    GenieMemoryConfig,
    GeniePadElementsSettingItem,
    GenieSettings,
} from '../../api/pushGenie';
import {
    AdvicesIcon,
    DiagnosisIcon,
    ExaminationFindingsIcon,
    InvestigationsIcon,
    LabVital,
    MedicinesIcon,
    SymptomsIcon,
} from '../../components/common/Icons';
import { CustomEntityTooltip } from '../../components/prescriptionPad/rightPanel/workspace/padV2/MedTableRenderer';
import { GAdvice, GExamination, GLabTest, GMedication, GSymptomsDiagnosis } from '../../store/db';
import { getItemFromMisc, getMySingleDoctorId, MISC_KEYS } from '../../store/idbHandler';
import { hashCode } from '../../store/utils';
import GenieConfigure from './GenieConfigure';

type Props = {
    path: string;
    url: string;
};

export const SYMPTOMS = 'symptoms';
export const DIAGNOSIS = 'diagnosis';
export const MEDICINES = 'medicines';
export const LAB_INVESTIGATIONS = 'lab-investigations';
export const ADVICES = 'advices';
export const LAB_VITALS = 'labVitals';
export const EXAMINATION_FINDINGS = 'examination-findings';
export const AUTO_COPY_FROM_PRESCRIPTION = 'auto-copy-from-prescription';
export const CUSTOM_ENTITY = 'custom';

export type ItemId =
    | typeof SYMPTOMS
    | typeof DIAGNOSIS
    | typeof MEDICINES
    | typeof LAB_INVESTIGATIONS
    | typeof EXAMINATION_FINDINGS
    | typeof ADVICES
    | typeof AUTO_COPY_FROM_PRESCRIPTION
    | typeof LAB_VITALS
    | typeof CUSTOM_ENTITY;

export const notMedicineObj: { [k: string]: string } = {
    device: 'Device',
    advice: 'Advice',
    nad: 'Not a Drug',
};

class GenieConfiguration extends PureComponent<Props> {
    static configs: {
        id: ItemId;
        name: string;
        icon: (className: string) => ReactNode;
        hideIsCodedColumn?: boolean;
        usageCountColumnWidth: string;
        hideDeleteIcon?: boolean;
        renderProperties?: boolean;
        renderForm?: boolean;
        renderGeneric?: boolean;
        renderManufacturer?: boolean;
        renderRemark?: boolean;
        nameColumnWidth?: string;
        getName: (item: GSymptomsDiagnosis | GMedication | GLabTest | GAdvice) => ReactNode;
        sorter: <T>(a: T, b: T) => number;
        filter?: <T>(items: T[], item: T) => T[];
        getDbKey?: <T>(a: T) => string;
        items?: GeniePadElementsSettingItem[];
        fetch?: () => Promise<GeniePadElementsSettingItem[]>;
        configTable?: boolean;
        genieKey: GenieKeys;
    }[] = [
        {
            id: MEDICINES,
            name: 'Medicines',
            icon: MedicinesIcon,
            usageCountColumnWidth: '10%',
            nameColumnWidth: '18%',
            hideDeleteIcon: true,
            hideIsCodedColumn: true,
            renderProperties: true,
            renderForm: true,
            renderGeneric: true,
            renderManufacturer: true,
            getName: (t): string | ReactNode => (
                <div className="flex items-start gap-x-6 break-all" id={'id-' + hashCode(t.id)}>
                    <div className="space-y-4">
                        <p className="flex space-x-5">
                            <p className="flex-1">{((t as unknown) as GMedication).name} </p>
                        </p>
                    </div>

                    <div className="relative top-5">
                        <CustomEntityTooltip
                            id={t.id}
                            kind={(t as GMedication).med_cat ? 'entity' : ''}
                        />
                    </div>
                </div>
            ),

            sorter: (a, b): number =>
                ((a as unknown) as GMedication).name.toLowerCase() <
                ((b as unknown) as GMedication).name.toLowerCase()
                    ? -1
                    : 1,
            genieKey: 'medications',
        },
        {
            id: SYMPTOMS,
            name: 'Symptoms',
            icon: SymptomsIcon,
            nameColumnWidth: '80%',
            usageCountColumnWidth: '30%',
            getName: (t): string => ((t as unknown) as GSymptomsDiagnosis).name,
            sorter: (a, b): number =>
                ((a as unknown) as GSymptomsDiagnosis).name.toLowerCase() <
                ((b as unknown) as GSymptomsDiagnosis).name.toLowerCase()
                    ? -1
                    : 1,
            genieKey: 'symptoms',
        },
        {
            id: DIAGNOSIS,
            name: 'Diagnosis',
            icon: DiagnosisIcon,
            usageCountColumnWidth: '30%',
            nameColumnWidth: '80%',
            getName: (t): string => ((t as unknown) as GSymptomsDiagnosis).name,
            sorter: (a, b): number =>
                ((a as unknown) as GSymptomsDiagnosis).name.toLowerCase() <
                ((b as unknown) as GSymptomsDiagnosis).name.toLowerCase()
                    ? -1
                    : 1,
            genieKey: 'diagnosis',
        },
        {
            id: LAB_INVESTIGATIONS,
            name: 'Lab Investigations',
            icon: InvestigationsIcon,
            usageCountColumnWidth: '30%',
            nameColumnWidth: '80%',
            hideIsCodedColumn: true,
            renderRemark: true,
            getName: (t): string | ReactNode => (
                <div className="flex items-start gap-x-6 break-all" id={'id-' + hashCode(t.id)}>
                    <div className="space-y-4">
                        <p className="flex space-x-5">
                            <p className="flex-1">{((t as unknown) as GLabTest).name} </p>
                        </p>
                    </div>

                    <div className="relative top-5">
                        <CustomEntityTooltip id={t.id} kind={'labtest'} />
                    </div>
                </div>
            ),
            sorter: (a, b): number =>
                ((a as unknown) as GLabTest).name.toLowerCase() <
                ((b as unknown) as GLabTest).name.toLowerCase()
                    ? -1
                    : 1,
            genieKey: 'labTests',
        },
        {
            id: EXAMINATION_FINDINGS,
            name: 'Examination Findings',
            icon: ExaminationFindingsIcon,
            usageCountColumnWidth: '30%',
            nameColumnWidth: '80%',
            getName: (t): string => ((t as unknown) as GExamination).name,
            sorter: (a, b): number =>
                ((a as unknown) as GExamination).name.toLowerCase() <
                ((b as unknown) as GExamination).name.toLowerCase()
                    ? -1
                    : 1,
            genieKey: 'examinations',
        },
        {
            id: ADVICES,
            name: 'Advices',
            icon: AdvicesIcon,
            hideIsCodedColumn: true,
            usageCountColumnWidth: '30%',
            nameColumnWidth: '80%',
            getName: (t): ReactNode => (
                <span dangerouslySetInnerHTML={{ __html: ((t as unknown) as GAdvice).text }}></span>
            ),
            // eslint-disable-next-line
            // @ts-ignore
            filter: (items: GAdvice[], item: GAdvice): GAdvice[] =>
                ((items as unknown) as GAdvice[]).filter(
                    (i) => i.text !== ((item as unknown) as GAdvice).text,
                ),
            sorter: (a, b): number =>
                (
                    ((a as unknown) as GAdvice).parsedText || ((a as unknown) as GAdvice).text
                ).toLowerCase() <
                (
                    ((b as unknown) as GAdvice).parsedText || ((b as unknown) as GAdvice).text
                ).toLowerCase()
                    ? -1
                    : 1,
            getDbKey: (a): string => ((a as unknown) as GAdvice).text,
            genieKey: 'advices',
        },
        {
            id: LAB_VITALS,
            name: 'Lab Results',
            icon: LabVital,
            usageCountColumnWidth: '20%',
            nameColumnWidth: '60%',
            getName: (t): string => ((t as unknown) as GSymptomsDiagnosis).name,
            sorter: (a, b): number =>
                ((a as unknown) as GSymptomsDiagnosis).name.toLowerCase() <
                ((b as unknown) as GSymptomsDiagnosis).name.toLowerCase()
                    ? -1
                    : 1,
            hideIsCodedColumn: true,
            genieKey: 'labVitals',
        },
        {
            id: CUSTOM_ENTITY,
            name: 'Custom Sections',
            icon: (className: string): JSX.Element => (
                <svg
                    role="img"
                    viewBox="0 0 11 13"
                    xmlns="http://www.w3.org/2000/svg"
                    className={`w-13 h-13 ${className}`}
                >
                    <path d="M10.5625 11.0234L8.28906 8.77344L10.5391 6.52344C10.7734 6.28906 10.7734 5.9375 10.5391 5.72656L10.0234 5.1875C9.78906 4.97656 9.4375 4.97656 9.22656 5.1875L7 7.4375L5.40625 5.84375C6.50781 5.32812 7.21094 4.08594 6.92969 2.72656C6.64844 1.41406 5.42969 0.5 4.09375 0.5H0.976562C0.578125 0.5 0.25 0.851562 0.25 1.27344V7.46094C0.25 7.76562 0.484375 8.02344 0.789062 8.02344H1.51562C1.82031 8.02344 2.05469 7.76562 2.05469 7.46094V6.14844H3.03906L5.66406 8.77344L3.41406 11.0234C3.20312 11.2344 3.20312 11.5859 3.41406 11.8203L3.95312 12.3594C4.16406 12.5703 4.51562 12.5703 4.72656 12.3594L7 10.0859L9.25 12.3594C9.46094 12.5703 9.8125 12.5703 10.0469 12.3594L10.5625 11.8203C10.7969 11.5859 10.7969 11.2344 10.5625 11.0234ZM2.10156 2.39844H4.14062C4.65625 2.39844 5.05469 2.82031 5.05469 3.33594C5.05469 3.85156 4.63281 4.27344 4.14062 4.27344H2.10156V2.39844Z"></path>
                </svg>
            ),
            usageCountColumnWidth: '30%',
            nameColumnWidth: '80%',
            configTable: true,
            genieKey: 'settings',
            getName: function (
                item: GSymptomsDiagnosis | GMedication | GLabTest | GAdvice,
            ): ReactNode {
                throw new Error('Function not implemented.');
            },
            sorter: function <T>(a: T, b: T): number {
                throw new Error('Function not implemented.');
            },
        },
        {
            id: AUTO_COPY_FROM_PRESCRIPTION,
            name: 'Auto Copy(Rx)',
            icon: (className: string): JSX.Element => (
                <svg
                    role="img"
                    viewBox="0 0 11 13"
                    xmlns="http://www.w3.org/2000/svg"
                    className={`w-13 h-13 ${className}`}
                >
                    <path d="M10.5625 11.0234L8.28906 8.77344L10.5391 6.52344C10.7734 6.28906 10.7734 5.9375 10.5391 5.72656L10.0234 5.1875C9.78906 4.97656 9.4375 4.97656 9.22656 5.1875L7 7.4375L5.40625 5.84375C6.50781 5.32812 7.21094 4.08594 6.92969 2.72656C6.64844 1.41406 5.42969 0.5 4.09375 0.5H0.976562C0.578125 0.5 0.25 0.851562 0.25 1.27344V7.46094C0.25 7.76562 0.484375 8.02344 0.789062 8.02344H1.51562C1.82031 8.02344 2.05469 7.76562 2.05469 7.46094V6.14844H3.03906L5.66406 8.77344L3.41406 11.0234C3.20312 11.2344 3.20312 11.5859 3.41406 11.8203L3.95312 12.3594C4.16406 12.5703 4.51562 12.5703 4.72656 12.3594L7 10.0859L9.25 12.3594C9.46094 12.5703 9.8125 12.5703 10.0469 12.3594L10.5625 11.8203C10.7969 11.5859 10.7969 11.2344 10.5625 11.0234ZM2.10156 2.39844H4.14062C4.65625 2.39844 5.05469 2.82031 5.05469 3.33594C5.05469 3.85156 4.63281 4.27344 4.14062 4.27344H2.10156V2.39844Z"></path>
                </svg>
            ),
            usageCountColumnWidth: '30%',
            nameColumnWidth: '80%',
            getName: (t): string => ((t as unknown) as GLabTest).name,
            sorter: (a, b): number =>
                ((a as unknown) as GLabTest).name.toLowerCase() <
                ((b as unknown) as GLabTest).name.toLowerCase()
                    ? -1
                    : 1,
            fetch: async (): Promise<GeniePadElementsSettingItem[]> =>
                (
                    await getItemFromMisc<GenieSettings>(
                        getMySingleDoctorId() || '',
                        MISC_KEYS.settings,
                    )
                ).padElements,
            configTable: true,
            genieKey: 'settings',
        },
    ];

    render(): ReactNode {
        const { path, url } = this.props;

        return (
            <div className="flex flex-col h-full overflow-hidden bg-white w-full">
                <Switch>
                    {GenieConfiguration.configs.map((config) => (
                        <Route exact path={`${path}/${config.id.toString()}/:filter`}>
                            <GenieConfigure itemId={config.id} name={config.name} url={url} />
                        </Route>
                    ))}
                    {GenieConfiguration.configs[0]?.id && (
                        <Route path="/">
                            <Redirect to={`${url}/${GenieConfiguration.configs[0]?.id}/all`} />
                        </Route>
                    )}
                </Switch>
            </div>
        );
    }
}

export default GenieConfiguration;
